import { ReanalyseDialog } from "@/components/analysis/reanalyse-dialog";
import { SuggestionCard } from "@/components/analysis/suggestion-card";
import { TimeSuggestionTable } from "@/components/analysis/time-suggestion-table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { LocalDate } from "@/components/ui/local-date";
import { toast } from "@/components/ui/use-toast";
import {
  type ApplyState,
  type SavedSuggestion,
  type Suggestion,
  type WorkItemIndexEntry,
  approveSuggestion,
  persistRejection,
} from "@/lib/analysis";
import { api } from "@/lib/api";
import {
  type DecisionState,
  type TimeSuggestion,
  type TimeSuggestionEdit,
  approveTimeSuggestion,
  createProposedWorkItem,
  editTimeSuggestion,
  initialDecisionState,
  rejectTimeSuggestion,
} from "@/lib/time-suggestions";
import {
  Link,
  Navigate,
  createFileRoute,
  useLoaderData,
  useNavigate,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";

/**
 * `/analysis/history/$id` — admin-only view of one saved AI Analysis run.
 * Shows the conversation that was analysed, the model's summary, and what the
 * run proposed, with each proposal's decision state. Anything still pending can
 * be decided here; the decision is recorded so it leaves its queue too.
 *
 * A run's output lands in two entities, so it renders as two sections: ticket
 * changes, then proposed time entries. They're not interchangeable — a time
 * proposal is confirmed by the person whose time it is.
 *
 * E2E behaviour contract lives in `e2e/analysis/analysis-history.spec.ts`.
 */

type SavedAnalysis = {
  id: string;
  sourceType: "meeting" | "whatsapp" | "email";
  sourceLabel: string | null;
  source: string;
  summary: string;
  suggestions: SavedSuggestion[];
  timeSuggestions: TimeSuggestion[];
  projectIndex: Record<string, string>;
  workItemIndex: Record<string, WorkItemIndexEntry>;
  createdAt: string;
  author: { id: string; name: string | null } | null;
  /** Set when this run is a re-run: what the operator asked it to do better. */
  guidance: string | null;
  /** The run this one answers, and the runs that answer it — a result is only
   *  readable once you can see it's the second (or third) opinion. */
  rerunOf: { id: string; createdAt: string } | null;
  reruns: Array<{ id: string; createdAt: string }>;
};

const SOURCE_LABEL: Record<SavedAnalysis["sourceType"], string> = {
  meeting: "meeting transcript",
  whatsapp: "WhatsApp chat",
  email: "client email",
};

/** Which list page to return to, based on where this analysis came from. */
function backTarget(sourceType?: SavedAnalysis["sourceType"]) {
  if (sourceType === "email") return "email";
  if (sourceType === "whatsapp") return "whatsapp";
  return "meetings";
}

/** Turn a saved suggestion's decision state into the card's apply state. */
function initialApplyState(s: SavedSuggestion): ApplyState {
  if (s.status === "approved") {
    return {
      status: "done",
      message: "Applied",
      link: s.appliedKind && s.appliedId ? { kind: s.appliedKind, id: s.appliedId } : undefined,
    };
  }
  if (s.status === "rejected") {
    return s.rejectedReason
      ? { status: "rejected", message: s.rejectedReason }
      : { status: "rejected" };
  }
  return { status: "idle" };
}

export const Route = createFileRoute("/_app/analysis/history/$id")({
  component: AnalysisDetailPage,
});

function AnalysisDetailPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const [analysis, setAnalysis] = useState<SavedAnalysis | null>(null);
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [drafts, setDrafts] = useState<Suggestion[]>([]);
  const [included, setIncluded] = useState<boolean[]>([]);
  const [applyStates, setApplyStates] = useState<ApplyState[]>([]);
  // Time proposals are keyed by id (not list position) and edited server-side,
  // so their state is a map, not a parallel array.
  const [timeRows, setTimeRows] = useState<TimeSuggestion[]>([]);
  const [timeDecisions, setTimeDecisions] = useState<Record<string, DecisionState>>({});

  useEffect(() => {
    if (user.globalRole !== "admin") return;
    let alive = true;
    api
      .get<SavedAnalysis>(`/api/analysis/${id}`)
      .then((res) => {
        if (!alive) return;
        setAnalysis(res);
        setDrafts(res.suggestions.map((s) => s.payload));
        setIncluded(res.suggestions.map((s) => s.status === "pending"));
        setApplyStates(res.suggestions.map(initialApplyState));
        setTimeRows(res.timeSuggestions ?? []);
        setTimeDecisions(
          Object.fromEntries(
            (res.timeSuggestions ?? []).map((t) => [t.id, initialDecisionState(t)]),
          ),
        );
      })
      .catch(() => {
        if (alive) setError("Couldn't load this analysis.");
      });
    // The project picker lets the admin retarget a create-item suggestion.
    api
      .get<{ projects: Array<{ id: string; name: string }> }>("/api/projects?pageSize=200")
      .then((res) => {
        if (alive) setProjects(res.projects.map((p) => ({ id: p.id, name: p.name })));
      })
      .catch(() => {
        /* the picker just stays empty */
      });
    return () => {
      alive = false;
    };
  }, [id, user.globalRole]);

  if (user.globalRole !== "admin") {
    return <Navigate to="/" />;
  }

  function patchDraft(index: number, patch: Partial<Suggestion>) {
    setDrafts((prev) => prev.map((d, i) => (i === index ? ({ ...d, ...patch } as Suggestion) : d)));
  }

  async function applyOne(index: number): Promise<void> {
    const s = drafts[index];
    const sid = analysis?.suggestions[index]?.id;
    if (!s) return;
    setApplyStates((prev) => prev.map((st, i) => (i === index ? { status: "applying" } : st)));
    const next = await approveSuggestion(sid, s);
    setApplyStates((prev) => prev.map((st, i) => (i === index ? next : st)));
  }

  async function rejectOne(index: number, reason?: string): Promise<void> {
    const sid = analysis?.suggestions[index]?.id;
    if (!sid) return;
    setApplyStates((prev) => prev.map((st, i) => (i === index ? { status: "rejecting" } : st)));
    try {
      await persistRejection(sid, reason);
      setApplyStates((prev) => prev.map((st, i) => (i === index ? { status: "rejected" } : st)));
    } catch {
      setApplyStates((prev) =>
        prev.map((st, i) =>
          i === index ? { status: "error", message: "Couldn't reject this suggestion." } : st,
        ),
      );
    }
  }

  // Named `suggestionId` rather than `id` on purpose: `id` in this component is
  // the ANALYSIS id, and shadowing it here would be a trap for the next edit.
  async function editTime(suggestionId: string, patch: TimeSuggestionEdit): Promise<void> {
    try {
      const updated = await editTimeSuggestion(suggestionId, patch);
      setTimeRows((prev) => prev.map((t) => (t.id === suggestionId ? updated : t)));
    } catch {
      setTimeDecisions((prev) => ({
        ...prev,
        [suggestionId]: { status: "error", message: "Couldn't save that change." },
      }));
    }
  }

  // Same "Create ticket" action `time-suggestion-queue.tsx` offers on the
  // live queue — a saved run's proposals are just as capable of naming an
  // unfiled ticket, and the table renders the button whenever a row isn't
  // decided yet, regardless of which page hosts it.
  async function createWorkItem(s: TimeSuggestion): Promise<void> {
    const res = await createProposedWorkItem(s);
    if (res.status === "done") {
      setTimeRows((prev) => prev.map((t) => (t.id === s.id ? res.suggestion : t)));
    } else {
      setTimeDecisions((prev) => ({ ...prev, [s.id]: { status: "error", message: res.message } }));
    }
  }

  async function decideTime(suggestionId: string, kind: "approve" | "reject"): Promise<void> {
    setTimeDecisions((prev) => ({
      ...prev,
      [suggestionId]: { status: kind === "approve" ? "approving" : "rejecting" },
    }));
    const next =
      kind === "approve"
        ? await approveTimeSuggestion(suggestionId)
        : await rejectTimeSuggestion(suggestionId);
    setTimeDecisions((prev) => ({ ...prev, [suggestionId]: next }));
  }

  async function applySelected() {
    for (let i = 0; i < drafts.length; i++) {
      if (included[i] && applyStates[i]?.status !== "done") {
        await applyOne(i);
      }
    }
  }

  async function deleteAnalysis() {
    setDeleting(true);
    try {
      await api.delete(`/api/analysis/${id}`);
      toast({ title: "Analysis deleted" });
      void navigate({
        to: "/agents/$slug/$tab",
        params: { slug: "analyst", tab: backTarget(analysis?.sourceType) },
      });
    } catch {
      toast({ title: "Couldn't delete the analysis.", variant: "destructive" });
      setDeleting(false);
    }
  }

  if (error) {
    return (
      <div className="w-full space-y-4">
        <p className="text-sm text-destructive">{error}</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/agents/$slug/$tab" params={{ slug: "analyst", tab: "meetings" }}>
            Back
          </Link>
        </Button>
      </div>
    );
  }

  if (!analysis) {
    return <p className="w-full text-sm text-muted-foreground">Loading…</p>;
  }

  const pendingCount = drafts.filter(
    (_, i) => included[i] && applyStates[i]?.status !== "done",
  ).length;
  const reruns = analysis.reruns ?? [];

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Saved analysis</h1>
          <p className="text-muted-foreground mt-1">
            From a {SOURCE_LABEL[analysis.sourceType]}
            {analysis.sourceLabel ? ` · ${analysis.sourceLabel}` : ""} ·{" "}
            <LocalDate value={analysis.createdAt} />
            {analysis.author?.name ? ` · ${analysis.author.name}` : ""}
          </p>
        </div>
        <div className="flex gap-2">
          {/* The second chance that needs no audio: the conversation is stored
              here for good, so a thin or wrong result can always be argued
              with — the note says what to do better. */}
          <ReanalyseDialog analysisId={analysis.id} />
          <Dialog>
            <DialogTrigger asChild>
              <Button type="button" variant="outline" size="sm" data-testid="delete-analysis">
                Delete
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Delete this analysis?</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                Deletes the analysis and its {analysis.suggestions.length + timeRows.length}{" "}
                proposal{analysis.suggestions.length + timeRows.length === 1 ? "" : "s"} (any
                already-applied work items / comments / time entries stay). This can't be undone.
              </p>
              <DialogFooter>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  disabled={deleting}
                  onClick={deleteAnalysis}
                  data-testid="delete-analysis-confirm"
                >
                  {deleting ? "Deleting…" : "Delete"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button asChild variant="outline" size="sm">
            <Link
              to="/agents/$slug/$tab"
              params={{ slug: "analyst", tab: backTarget(analysis.sourceType) }}
            >
              Back
            </Link>
          </Button>
        </div>
      </div>

      {/* Where this run sits in the chain. A re-run's proposals only make
          sense next to the note that asked for them, and an old run must say
          it has been superseded — otherwise two runs of one meeting read as
          two meetings. */}
      {(analysis.guidance || analysis.rerunOf || reruns.length > 0) && (
        <Card data-testid="analysis-rerun-info">
          <CardHeader>
            <CardTitle>{analysis.guidance ? "Re-run with a note" : "Re-run"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {analysis.guidance && (
              <p className="whitespace-pre-wrap" data-testid="analysis-guidance">
                “{analysis.guidance}”
              </p>
            )}
            {analysis.rerunOf && (
              <p className="text-muted-foreground">
                Ran again over the conversation of{" "}
                <Link
                  to="/analysis/history/$id"
                  params={{ id: analysis.rerunOf.id }}
                  className="underline"
                  data-testid="analysis-rerun-of"
                >
                  an earlier analysis
                </Link>{" "}
                (<LocalDate value={analysis.rerunOf.createdAt} />
                ). That one keeps its own proposals.
              </p>
            )}
            {reruns.length > 0 && (
              <p className="text-muted-foreground">
                This conversation was analysed again later:{" "}
                {reruns.map((r, i) => (
                  <span key={r.id}>
                    {i > 0 && ", "}
                    <Link
                      to="/analysis/history/$id"
                      params={{ id: r.id }}
                      className="underline"
                      data-testid="analysis-rerun-link"
                    >
                      <LocalDate value={r.createdAt} withTime />
                    </Link>
                  </span>
                ))}
                .
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {analysis.summary && (
        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap" data-testid="analysis-summary">
              {analysis.summary}
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Source conversation</CardTitle>
        </CardHeader>
        <CardContent>
          <details>
            <summary className="cursor-pointer text-sm text-muted-foreground">
              Show the analysed conversation
            </summary>
            <pre
              className="mt-3 max-h-96 overflow-auto whitespace-pre-wrap rounded-md bg-muted/50 p-3 text-xs"
              data-testid="analysis-source"
            >
              {analysis.source}
            </pre>
          </details>
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">
          {drafts.length === 0
            ? "No work item suggestions"
            : `${drafts.length} work item suggestion${drafts.length === 1 ? "" : "s"}`}
        </h2>
        {drafts.length > 0 && (
          <Button
            type="button"
            onClick={applySelected}
            disabled={pendingCount === 0}
            data-testid="apply-selected"
          >
            {`Apply selected (${pendingCount})`}
          </Button>
        )}
      </div>

      {drafts.map((s, i) => (
        <SuggestionCard
          // biome-ignore lint/suspicious/noArrayIndexKey: drafts are a fixed, index-stable list for one analysis
          key={i}
          suggestion={s}
          index={i}
          projects={projects}
          workItemIndex={analysis.workItemIndex}
          included={included[i] ?? false}
          applyState={applyStates[i] ?? { status: "idle" }}
          onToggle={(v) => setIncluded((prev) => prev.map((x, j) => (j === i ? v : x)))}
          onPatch={patchDraft}
          onApply={() => applyOne(i)}
          onReject={(reason) => rejectOne(i, reason)}
        />
      ))}

      {timeRows.length > 0 && (
        <>
          <h2 className="text-xl font-semibold" data-testid="time-suggestions-heading">
            {`${timeRows.length} proposed time ${timeRows.length === 1 ? "entry" : "entries"}`}
          </h2>
          <TimeSuggestionTable
            suggestions={timeRows}
            projects={projects}
            showOwner
            handlers={{
              decisionFor: (id) => timeDecisions[id] ?? { status: "idle" },
              onEdit: (id, patch) => void editTime(id, patch),
              onApprove: (id) => void decideTime(id, "approve"),
              onReject: (id) => void decideTime(id, "reject"),
              onCreateWorkItem: (s) => void createWorkItem(s),
            }}
          />
        </>
      )}
    </div>
  );
}
